import {parseSoftphoneMetricsLog, type WebRtcSample} from './webrtcSoftphoneLog'
import {parseTelemetryCallReportLog} from './webrtcCallReport'
import {enqueueWebRtcCallReport} from './telemetryOutbox'
import {getAgentTelemetryRuntimeConfig} from './runtimeConfig'

export type WebRtcTelemetrySender = (
  samples: WebRtcSample[],
  contactId: string | null,
) => void

const CONTACT_GRACE_MS = 45_000
const DEDUPE_TTL_MS = 120_000

let sender: WebRtcTelemetrySender | null = null
let contactId: string | null = null
let lastContactId: string | null = null
let lastContactAt = 0
let loggedAudioLevels = 0
const seen = new Map<string, number>()

/** Master tab registers this while CCP telemetry is allowed to send. */
export function setWebRtcTelemetrySender(next: WebRtcTelemetrySender | null): void {
  sender = next
}

/**
 * Current softphone contact. Cleared ids stay available for a short grace
 * so the end-of-call metrics batch still lands on that contact.
 */
export function setWebRtcTelemetryContactId(id: string | null): void {
  const trimmed = (id ?? '').trim()
  if (trimmed) {
    contactId = trimmed
    lastContactId = trimmed
    lastContactAt = Date.now()
    return
  }
  contactId = null
}

/**
 * CCP publishes per-second RTP stats on the parent event bus as
 * `softphone_stats`. The success log that carries the same payload stays
 * inside the CCP iframe, so the parent logger never sees it.
 */
export function ingestSoftphoneStatsEvent(payload: unknown): void {
  const stats = readStatsArray(payload)
  if (!stats) return
  const id = readEventContactId(payload)
  if (id) setWebRtcTelemetryContactId(id)
  ingestCcpTelemetryLog('sendSoftphoneMetrics success' + JSON.stringify(stats))
}

/** End-of-call summary, published as `softphone_report` on the same bus. */
export function ingestSoftphoneReportEvent(payload: unknown): void {
  const report = readReportObject(payload)
  if (!report) return
  const id = readEventContactId(payload)
  if (id) setWebRtcTelemetryContactId(id)
  ingestCcpTelemetryLog('sent TelemetryCallReport ' + JSON.stringify(report))
}

/** Called from the Streams logger. Never throws. */
export function ingestCcpTelemetryLog(text: string): void {
  try {
    ingestSoftphoneMetricsLog(text)
    ingestTelemetryCallReportLog(text)
  } catch {
    // A missed batch stays a gap. Do not throw into Streams.
  }
}

/** Called from the Streams logger. No-op unless the master tab registered a sender. */
export function ingestSoftphoneMetricsLog(text: string): void {
  if (!sender || !text || !text.includes('sendSoftphoneMetrics success')) return
  const samples = parseSoftphoneMetricsLog(text, Date.now())
  if (!samples || samples.length === 0) return

  const now = Date.now()
  pruneSeen(now)
  const fresh = samples.filter((sample) => {
    const key = [
      sample.direction,
      sample.rawTimestamp,
      sample.packetsLost ?? '',
      sample.packetsCount ?? '',
      sample.audioLevel ?? '',
      sample.jitterMs ?? '',
    ].join(':')
    if (seen.has(key)) return false
    seen.set(key, now)
    return true
  })
  if (fresh.length === 0) return
  noteRawAudioLevels(fresh)

  const id = activeContactId(now)
  try {
    sender(fresh, id)
  } catch {
    // The sender queues before it talks to the hub. A throw here drops this batch.
  }
}

function ingestTelemetryCallReportLog(text: string): void {
  if (!sender || !getAgentTelemetryRuntimeConfig()?.enabled) return
  if (!text || !text.includes('sent TelemetryCallReport')) return
  const report = parseTelemetryCallReportLog(text, Date.now())
  if (!report) return

  const now = Date.now()
  pruneSeen(now)
  const key = `report:${report.atMs}:${report.talkingTimeMs ?? ''}:${report.consecutiveNoAudioOutputPackets ?? ''}`
  if (seen.has(key)) return
  seen.set(key, now)

  const id = activeContactId(now)
  try {
    enqueueWebRtcCallReport({
      contactId: id,
      atMs: report.atMs,
      iceConnectionsLost: report.iceConnectionsLost,
      iceConnectionsFailed: report.iceConnectionsFailed,
      peerConnectionFailed: report.peerConnectionFailed,
      consecutiveNoAudioOutputPackets: report.consecutiveNoAudioOutputPackets,
      consecutiveNoAudioInputPackets: report.consecutiveNoAudioInputPackets,
      consecutiveLowOutputAudioLevel: report.consecutiveLowOutputAudioLevel,
      consecutiveLowInputAudioLevel: report.consecutiveLowInputAudioLevel,
      consecutiveAudioOutputMuteDurationS: report.consecutiveAudioOutputMuteDurationS,
      talkingTimeMs: report.talkingTimeMs,
      firstRtpTimeMs: report.firstRtpTimeMs,
      iceCollectionFailure: report.iceCollectionFailure,
      signallingConnectionFailure: report.signallingConnectionFailure,
      handshakingFailure: report.handshakingFailure,
      isMediaClusterPath: report.isMediaClusterPath,
      userAgent: report.userAgent || browserUserAgent(),
    })
  } catch {
    // Queuing failed. Do not throw into Streams.
  }
}

function readEventContactId(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null
  const id = (payload as {contactId?: unknown}).contactId
  return typeof id === 'string' && id.trim() ? id.trim() : null
}

function readStatsArray(payload: unknown): unknown[] | null {
  if (!payload || typeof payload !== 'object') return null
  const stats = (payload as {stats?: unknown}).stats
  return Array.isArray(stats) && stats.length > 0 ? stats : null
}

function readReportObject(payload: unknown): object | null {
  if (!payload || typeof payload !== 'object') return null
  const report = (payload as {report?: unknown}).report
  return report && typeof report === 'object' ? report : null
}

function activeContactId(now: number): string | null {
  return contactId || (now - lastContactAt <= CONTACT_GRACE_MS ? lastContactId : null)
}

function browserUserAgent(): string | undefined {
  try {
    const raw = navigator.userAgent?.trim()
    if (!raw) return undefined
    return raw.length <= 200 ? raw : raw.slice(0, 200)
  } catch {
    return undefined
  }
}

function noteRawAudioLevels(samples: WebRtcSample[]): void {
  if (loggedAudioLevels >= 3) return
  for (const sample of samples) {
    if (sample.audioLevel == null || loggedAudioLevels >= 3) continue
    console.info('[webrtc-telemetry] raw audioLevel', {
      direction: sample.direction,
      audioLevel: sample.audioLevel,
    })
    loggedAudioLevels += 1
  }
}

function pruneSeen(now: number): void {
  if (seen.size < 2000) return
  seen.forEach((at, key) => {
    if (now - at > DEDUPE_TTL_MS) seen.delete(key)
  })
}
